import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  const location = useLocation();
  const privacyRef = useRef(null);
  const cookieRef = useRef(null);
  const termsRef = useRef(null);
  const shippingRef = useRef(null);

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const refMap = {
        '#privacy': privacyRef,
        '#cookie': cookieRef,
        '#terms': termsRef,
        '#shipping': shippingRef
      };
      
      const targetRef = refMap[hash];
      if (targetRef && targetRef.current) {
        targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);

  const policies = [
    { id: 'privacy', ref: privacyRef, title: 'Privacy Policy' },
    { id: 'cookie', ref: cookieRef, title: 'Ad and Cookie Policy' },
    { id: 'terms', ref: termsRef, title: 'Terms and Conditions' },
    { id: 'shipping', ref: shippingRef, title: 'Shipping and Returns' }
  ];

  const privacyContent = `
    At Panchali Design Hub, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your personal information.

    Information We Collect:
    • Personal identification information (Name, email address, phone number)
    • Shipping and billing addresses
    • Payment information
    • Shopping preferences and history
    • Device and browser information

    How We Use Your Information:
    • Process your orders and payments
    • Communicate about your orders and account
    • Send personalized recommendations
    • Improve our services and website
    • Prevent fraud and enhance security

    Data Protection:
    We implement various security measures to maintain the safety of your personal information when you place an order or access your personal information.
  `;

  const cookieContent = `
    Our Cookie Policy explains how we use cookies and similar technologies on our website.

    Types of Cookies We Use:
    • Essential cookies: Required for basic site functionality
    • Analytics cookies: Help us understand how visitors interact with our website
    • Advertising cookies: Used to deliver relevant advertisements
    • Preference cookies: Remember your settings and choices

    Advertisement Partners:
    We partner with trusted third-party advertisers to show you relevant ads. These partners may use cookies and similar technologies to collect information about your browsing activities.

    Managing Cookies:
    You can control and/or delete cookies as you wish. You can delete all cookies that are already on your computer and you can set most browsers to prevent them from being placed.
  `;

  const termsContent = `
    Welcome to Panchali Design Hub. By accessing our website, you agree to these terms and conditions.

    Intellectual Property:
    All content on this website, including images, text, and designs, is the property of Panchali Design Hub and is protected by copyright laws.

    User Account:
    • You are responsible for maintaining the confidentiality of your account
    • You must be 18 years or older to create an account
    • We reserve the right to terminate accounts that violate our policies

    Product Information:
    • All product descriptions are accurate to the best of our knowledge
    • Colors may vary slightly due to display settings
    • We reserve the right to modify prices without notice

    Prohibited Activities:
    • Using the site for illegal purposes
    • Attempting to gain unauthorized access
    • Interfering with site security
  `;

  const shippingContent = `
    Shipping Policy:
     Processing Time: Orders are processed within 1-2 business days. During high-volume periods or special sales, processing times may be slightly delayed.

    • Domestic Orders (Within India):
      - Standard Shipping (5–7 business days)
      - Express Shipping (2–4 business days)
      - Free Shipping on orders above ₹999 (India only)
      - We currently ship across India. For international shipping inquiries, please contact us at care@panchalidesignhub.com
      - Delays may occur due to weather, festive seasons, or courier issues. We appreciate your patience.


    
    • International Orders:
      - Standard: 10-15 business days
      - Express: 5-7 business days
      - Customs duties may apply

    Returns and Exchanges:
    • 30-day return policy for unused items
    • Items must be in original packaging
    • Return shipping cost:
      - Free for defective items
      - Customer bears cost for size/fit issues
    
    Non-Returnable Items:
    • Customized products
    • Intimate wear
    • Sale items marked as 'Final Sale'

    Refund Process:
    • Refunds processed within 5-7 business days
    • Original payment method will be used
    • Store credit option available
  `;

  return (
    <>
      <Helmet>
        <title>Policies | Panchali Design Hub</title>
        <meta name="description" content="Privacy Policy, Terms and Conditions, Shipping Information for Panchali Design Hub" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Quick Navigation */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 bg-white rounded-xl shadow-sm p-6"
        >
          <h2 className="text-xl font-semibold mb-4">Quick Navigation</h2>
          <div className="flex flex-wrap gap-4">
            {policies.map((policy) => (
              <a
                key={policy.id}
                href={`#${policy.id}`}
                className="px-4 py-2 bg-gray-50 hover:bg-indigo-50 text-gray-700 hover:text-indigo-900 rounded-lg transition-colors"
              >
                {policy.title}
              </a>
            ))}
          </div>
        </motion.div>

        {/* Policy Sections */}
        <div className="space-y-16">
          {/* Privacy Policy */}
          <section ref={privacyRef} id="privacy" className="scroll-mt-24">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="prose max-w-none"
            >
              <h2 className="text-2xl font-bold mb-6">Privacy Policy</h2>
              <p className="text-gray-600 mb-4">Last updated: {new Date().toLocaleDateString()}</p>
              <div className="whitespace-pre-line text-gray-700">
                {privacyContent}
              </div>
            </motion.div>
          </section>

          {/* Ad and Cookie Policy */}
          <section ref={cookieRef} id="cookie" className="scroll-mt-24">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="prose max-w-none"
            >
              <h2 className="text-2xl font-bold mb-6">Ad and Cookie Policy</h2>
              <p className="text-gray-600 mb-4">Last updated: {new Date().toLocaleDateString()}</p>
              <div className="whitespace-pre-line text-gray-700">
                {cookieContent}
              </div>
            </motion.div>
          </section>

          {/* Terms and Conditions */}
          <section ref={termsRef} id="terms" className="scroll-mt-24">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="prose max-w-none"
            >
              <h2 className="text-2xl font-bold mb-6">Terms and Conditions</h2>
              <p className="text-gray-600 mb-4">Last updated: {new Date().toLocaleDateString()}</p>
              <div className="whitespace-pre-line text-gray-700">
                {termsContent}
              </div>
            </motion.div>
          </section>

          {/* Shipping and Returns */}
          <section ref={shippingRef} id="shipping" className="scroll-mt-24">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="prose max-w-none"
            >
              <h2 className="text-2xl font-bold mb-6">Shipping and Returns</h2>
              <p className="text-gray-600 mb-4">Last updated: {new Date().toLocaleDateString()}</p>
              <div className="whitespace-pre-line text-gray-700">
                {shippingContent}
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
